import { Component, DestroyRef, ElementRef, EventEmitter, Output, ViewChild, ChangeDetectionStrategy, inject } from '@angular/core';
import { WillyGreetingService } from '../../@services/front/willy-greeting.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, NavigationEnd } from '@angular/router';
import { SharedModule } from '../../../@commons/ui-components/shared.module';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { AppMenuItemComponent } from './app-menu-item/app-menu-item.component';
import { AppMenuCentralComponent } from './app-menu-central/app-menu-central.component';
import { MenuAiActionsToastComponent } from '../../@toasts/menu-ai-actions-toast/menu-ai-actions-toast.component';
import { HomeIconComponent } from '../../../@icons/home-icon.component';
import { GardenIconComponent } from '../../../@icons/garden-icon.component';
import { AccountIconComponent } from '../../../@icons/account-icon.component';
import { CalendarClockIconComponent } from '../../../@icons/calendar-clock-icon.component';
import { NativeBridgeService } from '../../@services/webview/native-bridge.service';
import { SafeAreaService } from '../../@services/webview/safe-area.service';
import { WillyChatService } from '../../@services/front/willy-chat.service';
import { OverlayViewportService } from '../../@services/front/overlay-viewport.service';

/**
 * Hauteur de la barre d'onglets au-dessus de sa marge basse : la barre (72 px)
 * et la carotte qui en dépasse (27 px). Willy y cale ses actions, sous le pouce.
 * Recalculée comme la barre se pose (`safeAreaBottom`) ; à remplacer par
 * `--vw-bar-h`, mesurée, quand fix/place-barre sera fusionnée.
 */
export const TAB_BAR_HEIGHT_PX = 99;

/**
 * Section de l'app (premier segment de l'URL) → onglet à allumer. Les écrans
 * ouverts depuis l'accueil (météo, lune, conseils, notifications) restent sous
 * « Accueil » ; « Mes tâches » (/agenda) vit sous « Calendrier ».
 */
const TAB_BY_SECTION: Record<string, string> = {
  home: 'home',
  weather: 'home',
  lunar: 'home',
  articles: 'home',
  notifications: 'home',
  calendar: 'calendar',
  agenda: 'calendar',
  garden: 'garden',
  profile: 'profile',
  feedback: 'profile',
};

/** Onglet correspondant à une URL (`/garden/list?x=1` → `garden`). */
export function tabForUrl(url: string): string {
  const section = url.split(/[?#]/)[0].split('/').find((segment) => segment.length > 0);
  return (section && TAB_BY_SECTION[section]) || 'home';
}

@Component({
  selector: 'vw-app-menu',
  imports: [
    SharedModule,
    CommonModule,
    TranslatePipe,
    AppMenuItemComponent,
    AppMenuCentralComponent,
    MenuAiActionsToastComponent,
    HomeIconComponent,
    GardenIconComponent,
    AccountIconComponent,
    CalendarClockIconComponent,

  ],
  templateUrl: './app-menu.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './app-menu.component.scss'
})
export class AppMenuComponent {
  /**
   * Onglet allumé, toujours DÉRIVÉ de l'URL courante, jamais reçu ni mémorisé.
   *
   * Il était fourni par le gabarit (état de NavigationService, qui ne
   * reconnaît que `home`, `agenda`, `garden`, `profile` et relit l'onglet de
   * la session précédente) : au premier rendu, ce binding écrasait l'URL et un
   * lien direct vers `/garden/list` ou `/calendar` allumait « Accueil ».
   */
  currentScreen: string;
  @Output() screenChange = new EventEmitter<string>();

  isAIModalOpen: boolean = false;
  inputIsFocus: boolean = false;

  safeAreaBottom: number = 32;

  @ViewChild('carrotButton') private carrot?: AppMenuCentralComponent;
  @ViewChild('willy') private willyHost?: ElementRef<HTMLElement>;

  /** La barre est recréée à la reconnexion : ses abonnements à des flux racine meurent avec elle (DEV-10). */
  private readonly destroyRef = inject(DestroyRef);
  /** Willy salue dans la carotte, une fois par jour au plus (demandé par l'accueil). */
  readonly willyGreeting = inject(WillyGreetingService);

  constructor(private router: Router, private nativeBridge: NativeBridgeService, private safeAreaService: SafeAreaService,
    private willyChatService: WillyChatService, private overlayViewport: OverlayViewportService) {
    // Démarrage à froid : la navigation est déjà terminée quand la barre naît.
    this.currentScreen = tabForUrl(this.router.url);
    // Écoute les événements de navigation pour mettre à jour `currentScreen`
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.isAIModalOpen = false;
        this.currentScreen = tabForUrl(event.urlAfterRedirects);

        // Ouverture différée demandée depuis un écran sans menu (ex. « Diagnostiquer
        // cette plante » sur le détail d'une plante) : on ouvre puis on injecte le
        // pré-remplissage une fois le chatbot monté (timeout → après ce cycle de rendu).
        const pendingPrefill = this.willyChatService.takePendingOpen();
        if (pendingPrefill !== null) {
          this.isAIModalOpen = true;
          setTimeout(() => this.willyChatService.openChat(pendingPrefill), 0);
        }
      }
    });
    this.safeAreaService.safeAreaData$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      this.safeAreaBottom = data.bottom || 32;
    });
    // Open the AI chat when requested from elsewhere (e.g. "Diagnose this plant")
    this.willyChatService.openChat$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.isAIModalOpen = true;
    });
  }

  /** Place que la barre prend en bas de l'écran, marge basse comprise. */
  get barSpace(): number {
    return TAB_BAR_HEIGHT_PX + this.safeAreaBottom;
  }

  /**
   * Willy ouvert, la barre s'efface :
   * - pendant la saisie : le clavier prend le bas, comme dans une messagerie
   *   (sur Android, la mise en page rétrécit et la barre monterait sur le clavier) ;
   * - sous une feuille ouverte par-dessus Willy (paywall), dont elle couvrirait le bas.
   */
  get barHidden(): boolean {
    return this.isAIModalOpen && (this.inputIsFocus || this.overlayViewport.layers() > 1);
  }

  changeScreen(screen: string) {
    this.isAIModalOpen = false;
    if (screen === this.currentScreen) {
      this.reselectTab(screen);
      return;
    }
    this.router.navigate([`/${screen}`]); // Navigation via le Router
  }

  /**
   * Toucher l'onglet déjà actif, comme sur iOS et Android : le premier toucher
   * remonte en haut de la page, le suivant (déjà en haut) ramène à la racine
   * de l'onglet. Sans animation pour qui a demandé moins de mouvement.
   */
  private reselectTab(screen: string): void {
    if (window.scrollY > 0) {
      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
      return;
    }
    this.router.navigate([`/${screen}`]);
  }

  onClickProfile() {
    this.changeScreen('profile');
  }

  /** La carotte ouvre Willy, et le referme. */
  openAIPopup() {
    this.nativeBridge.haptic('selection');
    if (this.isAIModalOpen) {
      this.onAIModalClosed();
      return;
    }
    this.isAIModalOpen = true;
  }

  onAIModalClosed() {
    this.isAIModalOpen = false;
    this.inputIsFocus = false;
    this.focusCarrot();
  }

  /**
   * Après la fermeture, le focus revient à la carotte, sauf si la feuille l'a
   * déjà rendu à qui l'avait ouverte. Au doigt, le bouton ne prend jamais le
   * focus : sans cela, il tomberait sur la page.
   */
  private focusCarrot() {
    setTimeout(() => {
      const active = document.activeElement;
      if (!active || active === document.body || this.willyHost?.nativeElement.contains(active)) {
        this.carrot?.focus();
      }
    });
  }

  onInputFocus() {
    this.inputIsFocus = true;
  }

  onInputBlur() {
    this.inputIsFocus = false;
  }
}
